import { APP_INITIALIZER, ErrorHandler, inject, LOCALE_ID, ModuleWithProviders, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy, Router } from '@angular/router';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, indexedDBLocalPersistence, initializeAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { connectFunctionsEmulator, getFunctions, provideFunctions } from '@angular/fire/functions';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HttpClientModule } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/angular-ivy';
import { ENVIRONMENT } from '@core/core.constants';
import { SharedModule } from '@shared/shared.module';
import { register } from 'swiper/element';
import { LottieModule } from 'ngx-lottie';
import player from 'lottie-web';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { OnboardingWrappersModule } from './onboarding/onboarding-wrappers.module';
import { CheckoutWrappersModule } from './checkout/checkout-wrappers.module';

register();

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    IonicModule.forRoot({
      hardwareBackButton: true,
      swipeBackEnabled: false,
      rippleEffect: false,
      mode: 'md',
    }),
    provideFirebaseApp(() => {
      const environment = inject<any>(ENVIRONMENT);
      return initializeApp(environment.firebase);
    }),
    provideAuth(() => {
      if (Capacitor.isNativePlatform()) {
        return initializeAuth(getApp(), {
          persistence: indexedDBLocalPersistence,
        });
      }
      return getAuth();
    }),
    provideFirestore(() => getFirestore()),
    provideFunctions(() => {
      const environment = inject<any>(ENVIRONMENT);
      const functions = getFunctions(getApp(), 'europe-west1');
      if (environment.emulator) {
        connectFunctionsEmulator(functions, 'localhost', 5001);
      }
      return functions;
    }),
    provideStorage(() => getStorage()),
    SharedModule,
    LottieModule.forRoot({ player: () => player }),
    OnboardingWrappersModule,
    CheckoutWrappersModule,
  ],
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: LOCALE_ID, useValue: 'es-ES' },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class BaseWebModule {
  static forRoot(environment: any): ModuleWithProviders<BaseWebModule> {
    return {
      ngModule: BaseWebModule,
      providers: [{ provide: ENVIRONMENT, useValue: environment }],
    };
  }
}
